import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  TextField,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createRoom } from '../../store/slices/roomsSlice';
import { fetchWinningPatterns } from '../../store/slices/winningPatternsSlice';
import { RootState } from '../../store';
import { RoomForm, RoomStake } from '../../types';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
  stake: yup.number().oneOf([5, 10, 20, 30, 50, 100, 200, 500, 1000]).required('Stake is required'),
  winningPattern: yup.string().required('Winning pattern is required'),
  cardCount: yup.number().min(1).max(1000).required('Card count is required'),
});

type FormData = RoomForm;

interface RoomFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RoomFormModal: React.FC<RoomFormModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(false);

  // Get winning patterns from Redux store
  const { patterns: winningPatterns } = useSelector((state: RootState) => state.winningPatterns);

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      stake: 50,
      winningPattern: '',
      cardCount: 50,
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        stake: 50,
        winningPattern: '',
        cardCount: 50,
      });
      // Fetch winning patterns when modal opens
      dispatch(fetchWinningPatterns({ page: 1, limit: 100 }) as any);
    }
  }, [open, reset, dispatch]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      console.log('Creating room with data:', data);
      const result = await dispatch(createRoom(data) as any);
      console.log('Create room result:', result);
      
      if (result.payload) {
        toast.success('Room created successfully!');
        onSuccess();
        onClose();
      } else {
        console.error('Create room failed:', result.payload);
        toast.error('Failed to create room');
      }
    } catch (error) {
      console.error('Create room error:', error);
      toast.error('An error occurred while creating room');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const stakeOptions: RoomStake[] = [5, 10, 20, 30, 50, 100, 200, 500, 1000];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
     >
      <DialogTitle sx={{ 
        padding: { xs: 2, sm: 3 },
        paddingBottom: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Create New Room
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
        padding: { xs: 2, sm: 3 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 },
        }
      }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Creating a new room will generate the specified number of Bingo cards for players to purchase.
          </Alert>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="stake"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.stake}>
                  <InputLabel>Room Stake</InputLabel>
                  <Select {...field} label="Room Stake">
                    {stakeOptions.map((stake) => (
                      <MenuItem key={stake} value={stake}>
                        {formatCurrency(stake)}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.stake && (
                    <Typography variant="caption" color="error">
                      {errors.stake.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="winningPattern"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.winningPattern}>
                  <InputLabel>Winning Pattern</InputLabel>
                  <Select {...field} label="Winning Pattern">
                    {winningPatterns.map((pattern) => (
                      <MenuItem key={pattern.id} value={pattern.id}>
                        {pattern.name} - {pattern.description}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.winningPattern && (
                    <Typography variant="caption" color="error">
                      {errors.winningPattern.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="cardCount"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Number of Cards"
                  type="number"
                  placeholder="50"
                  error={!!errors.cardCount}
                  helperText={errors.cardCount?.message || 'Number of Bingo cards to generate for this room'}
                  inputProps={{
                    min: 1,
                    max: 1000,
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: { xs: 1, sm: 0 },
        padding: { xs: 2, sm: 1 },
        '& > *': {
          width: { xs: '100%', sm: 'auto' },
        }
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Creating...' : 'Create Room'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoomFormModal; 